
/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

import { 
    ICrfConditionParameters, 
    PatientContext, 
    InstitutionContext, 
    CollaboratingGroupContext, 
    MasterGroupContext, 
    RouteLoading,
    PageLayout,
    FormBreadcrumbs,
    CrfForm,
    GroupedFields,
    GroupedField,
    CrfCondition,
    ToggleButtonGroup,
    Text,
    Select,
    DatePicker
} from '@ngt/opms';
import React, { useContext } from 'react';


/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import * as Dtos from '../../../api/dtos';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import { ConditionMode, ValidateOn } from '@ngt/forms-core';
import { AppSettings } from '../../../AppSettings';
import { Typography } from '@mui/material';

/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */
interface ITissueCollectionFormProps { }

/*
 * ----------------------------------------------------------------------------------
 * Functions
 * ----------------------------------------------------------------------------------
 */
const tissueSampleSpecified = ({ formState }: ICrfConditionParameters<Dtos.TissueCollection>) => formState?.value?.tissueSampleSubmitted === Dtos.LookupYesNoType.Yes;
const tissueSampleNotSpecified = ({ formState }: ICrfConditionParameters<Dtos.TissueCollection>) => formState?.value?.tissueSampleSubmitted === Dtos.LookupYesNoType.No;

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */
const TissueCollectionForm: React.FunctionComponent<ITissueCollectionFormProps> = () => {
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);

    const {
        permissions: [
            canUpdateCrf
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }


    return (
        <>
            {
                <PageLayout
                    breadcrumbs={<FormBreadcrumbs />}
                >
                    <CrfForm
                        validateOn={ValidateOn.onChange}
                        readOnly={!canUpdateCrf}
                        debug={AppSettings.debug}
                    >
                        <Typography sx={{px: 2, py: 3}}>
                            Tissue samples are to be stored locally and made available on request. <strong>Please confirm availability of FFPE block and record the ‘Date of Sample Collection’ and ‘Local Laboratory ID’ below.</strong>&nbsp;
                            BCT will request the bulk shipment of tumour tissue specimens at the time of planned analysis. If the FFPE block can not be provided at that time, freshly cut slides will be requested.
                        </Typography>
                        <GroupedFields>
                            <GroupedField
                                propertyPath="tissueSampleSubmitted"
                                component={ToggleButtonGroup}
                            />
                            {
                                <CrfCondition
                                    mode={ConditionMode.Show}
                                    condition={tissueSampleNotSpecified}
                                    subscription={{ value: true }}
                                >
                                    <GroupedField
                                        propertyPath="tissueSampleSubmittedSpecify"
                                        component={Text}
                                    />
                                </CrfCondition>
                            }
                        </GroupedFields>

                        <CrfCondition
                            mode={ConditionMode.Enable}
                            condition={tissueSampleSpecified}
                            subscription={{ value: true }}
                        >
                            <GroupedField
                                propertyPath="tissueCollectionTimepoint"
                                component={Select}
                            />
                            <GroupedField
                                propertyPath="dateOfCollection"
                                component={DatePicker}
                            />
                            <GroupedField
                                propertyPath="blockNumbersForFFPE"
                                component={Text}
                            />
                        </CrfCondition>
                    </CrfForm>
                </PageLayout>
            }
        </>
    );
}


/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default TissueCollectionForm;
